import { StrictMode, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import './i18n'
import '@khmyznikov/pwa-install'

import { PDFViewer } from './components/simple-pdf-viewer'
import { ErrorBoundary } from './components/simple-pdf-viewer/ErrorBoundary'
import { OfflineBanner } from './components/simple-pdf-viewer/OfflineBanner'
import { useFontLanguage } from './utils/fontLanguage'

function findCssVarSources(variableName: string) {
  const sources: string[] = []
  Array.from(document.styleSheets).forEach((sheet) => {
    const source = (sheet as CSSStyleSheet).href || 'inline stylesheet'
    try {
      Array.from((sheet as CSSStyleSheet).cssRules || []).forEach((rule) => {
        if ('cssText' in rule && rule.cssText.includes(variableName)) {
          sources.push(source)
        }
      })
    } catch (error) {
      // stylesheet may be cross-origin or inaccessible
    }
  })
  return sources
}

function findCssClassSources(className: string) {
  const selector = `.${className}`
  const sources: string[] = []
  Array.from(document.styleSheets).forEach((sheet) => {
    const source = (sheet as CSSStyleSheet).href || 'inline stylesheet'
    try {
      Array.from((sheet as CSSStyleSheet).cssRules || []).forEach((rule) => {
        if (rule instanceof CSSStyleRule) {
          const selectors = rule.selectorText.split(',').map((s) => s.trim())
          if (selectors.includes(selector)) {
            sources.push(`${source} -> ${rule.selectorText}`)
          }
        }
      })
    } catch (error) {
      // stylesheet may be cross-origin or inaccessible
    }
  })
  return sources
}

function App() {
  useFontLanguage()

  useEffect(() => {
    const root = document.documentElement
    const rootStyles = getComputedStyle(root)
    const variableNames = [
      '--ep-background-app',
      '--ep-background-surface',
      '--ep-background-surface-alt',
      '--ep-foreground-primary',
      '--ep-foreground-secondary',
      '--ep-border-default',
      '--color-bg-surface',
      '--color-fg-primary',
    ]
    const debugClasses = [
      'bg-bg-surface',
      'text-fg-primary',
      'border-border-default',
    ]

    const variables = Object.fromEntries(
      variableNames.map((name) => [name, rootStyles.getPropertyValue(name).trim() || '(missing)'])
    )

    console.groupCollapsed('[Theme Debug] CSS variable sources and computed values')
    console.log('documentElement classes:', root.className)
    console.log('body classes:', document.body.className)
    console.log('root variables:', variables)
    console.log('loaded style sheets:', Array.from(document.styleSheets).map((sheet) => (sheet as CSSStyleSheet).href || 'inline stylesheet'))

    const sample = document.createElement('div')
    sample.className = 'border border-border-default bg-bg-surface text-fg-primary p-2 invisible fixed top-0 left-0'
    sample.style.pointerEvents = 'none'
    sample.style.zIndex = '-1'
    document.body.appendChild(sample)

    const sampleStyles = getComputedStyle(sample)
    console.log('sample element class list:', sample.className)
    console.log('sample element computed styles:', {
      backgroundColor: sampleStyles.backgroundColor,
      color: sampleStyles.color,
      borderColor: sampleStyles.borderColor,
      borderStyle: sampleStyles.borderStyle,
      borderWidth: sampleStyles.borderWidth,
    })

    const classSources = Object.fromEntries(
      debugClasses.map((className) => [className, findCssClassSources(className)])
    )
    console.log('debug class selector sources:', classSources)

    document.body.removeChild(sample)

    const variableSources = Object.fromEntries(
      variableNames.map((name) => [name, findCssVarSources(name)])
    )
    console.log('variable definitions found in stylesheets:', variableSources)
    console.groupEnd()
  }, [])

  return (
    <ErrorBoundary>
      <OfflineBanner />
      <div className="h-screen w-screen overflow-hidden">
        <PDFViewer />
      </div>
    </ErrorBoundary>
  )
}

async function run() {
  if ('launchQueue' in window) {
    (window as any).launchQueue.setConsumer(async (params: any) => {
      const handle = params.files?.[0]
      if (!handle) return
      try {
        const file = await handle.getFile()
        const buffer = await file.arrayBuffer();
        (window as any).__opakorLaunchFile__ = { name: file.name, buffer }
      } catch {}
    })
  }

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <App />
    </StrictMode>,
  )
}

run()
